import { AIProcessCurrentStep, AiProcessStatus } from '@/features/AiChat/model/api/chat/types';
import { getChatSessionStatusApi } from '@/features/AiChat/model/api/chat';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAiSessionStatusParams {
  canPoll?: boolean;
  onSuccess: () => void;
  scopeKey?: string;
}

export const useAiSessionStatus = ({ canPoll = true, onSuccess, scopeKey }: UseAiSessionStatusParams) => {
  const [aiProcessStatus, setAiProcessStatus] = useState<AiProcessStatus>(AiProcessStatus.IDLE);
  const [aiProcessCurrentStep, setAiProcessCurrentStep] = useState<AIProcessCurrentStep>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollGenerationRef = useRef(0);
  const pollScopeRef = useRef({ canPoll, scopeKey });
  pollScopeRef.current = { canPoll, scopeKey };

  const stopPolling = useCallback((): void => {
    pollGenerationRef.current += 1;
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    stopPolling();
    setAiProcessCurrentStep(undefined);
    setAiProcessStatus(AiProcessStatus.IDLE);
    return stopPolling;
  }, [canPoll, scopeKey, stopPolling]);

  const startPolling = useCallback((sessionId: number, delay = 1000): void => {
    stopPolling();
    if (!pollScopeRef.current.canPoll) return;

    const generation = pollGenerationRef.current;
    const scope = pollScopeRef.current.scopeKey;
    const isCurrentPoll = (): boolean => generation === pollGenerationRef.current
      && pollScopeRef.current.canPoll
      && scope === pollScopeRef.current.scopeKey;
    setAiProcessStatus(AiProcessStatus.RUNNING);

    const fetchStatus = async (): Promise<void> => {
      if (!isCurrentPoll()) return;

      try {
        const response = await getChatSessionStatusApi(sessionId);
        if (!isCurrentPoll()) return;

        setAiProcessCurrentStep(response.current_step);

        if (response.status === AiProcessStatus.RUNNING) {
          setAiProcessStatus(AiProcessStatus.RUNNING);
          timeoutRef.current = setTimeout(() => void fetchStatus(), delay);
        } else if (response.status === AiProcessStatus.IDLE || response.status === AiProcessStatus.FAILED) {
          stopPolling();

          setAiProcessStatus(response.status);
          setAiProcessCurrentStep(undefined);

          if (response.status === AiProcessStatus.IDLE) onSuccess();
        }
      } catch {
        if (isCurrentPoll()) {
          stopPolling();
          setAiProcessCurrentStep(undefined);
          setAiProcessStatus(AiProcessStatus.FAILED);
        }
      }
    };

    void fetchStatus();
  }, [onSuccess, stopPolling]);

  return {
    aiProcessCurrentStep,
    aiProcessStatus,
    setAiProcessStatus,
    startPolling,
    stopPolling
  };
};